{
  "dashboard": {
    "title": "My Competitions",
    "description": "See your registration, team, and submission progress.",
    "loading": "Loading competitions…",
    "status": "Status",
    "deadlineNote": "Submission deadline is shown in the competition details.",
    "openCompetition": "Enter competition →",
    "groups": {
      "active": "In progress",
      "completed": "Completed"
    },
    "card": {
      "registrationNeedsUpdate": "Registration needs an update: {{fields}}",
      "deadline": "Due",
      "completeRegistration": "Complete registration",
      "setUpTeam": "Set up team",
      "startSubmission": "Start your submission",
      "continueSubmission": "Continue your submission",
      "checkAndSubmit": "Check and submit",
      "correctionRequired": "View and fix your entry",
      "viewReceipt": "View submission receipt",
      "viewSubmitted": "View submitted work",
      "viewResults": "View results",
      "enterCompetition": "Enter competition"
    },
    "empty": {
      "title": "You haven't joined any competitions yet",
      "description": "Explore competitions that are currently open — they'll show up here once you register.",
      "cta": "Explore competitions"
    }
  },
  "competition": {
    "loading": "Loading competition…",
    "unavailable": "Competition unavailable.",
    "registrationForm": {
      "loading": "Loading…",
      "save": "Save",
      "saved": "Registration form saved.",
      "saveError": "Could not save the registration form.",
      "saving": "Saving…",
      "confirmAndRegister": "Confirm and complete registration",
      "saveChanges": "Save changes",
      "completedBadge": "Done",
      "needsUpdate": "Needs update",
      "needsUpdateDetail": "Still to fill in: {{fields}}",
      "fillIn": "Fill in registration",
      "incomplete": "Pending",
      "lastUpdated": "Last updated: {{date}}",
      "noFormNeeded": "No additional registration details needed",
      "viewOrEdit": "View / edit registration details",
      "cancelEdit": "Cancel editing",
      "cardTitle": "Registration details",
      "autosaved": "✓ Autosaved {{time}}",
      "autosavePending": "Some required fields are still empty. Your answers will be saved automatically once they are complete.",
      "autosaving": "Saving…",
      "autosaveError": "⚠ Autosave failed",
      "versionConflict": "This registration was updated in another window.",
      "reloadLatest": "Reload latest data",
      "deadlinePassed": "The registration deadline has passed. This registration can no longer be saved or edited."
    },
    "registrationSuccess": {
      "title": "Registration complete",
      "description": "Your registration details have been submitted.",
      "later": "I'll do this later"
    },
    "myTeam": "My team",
    "memberCount": "{{count}} members",
    "manageTeam": "Manage team",
    "createOrJoinTeam": "Create or join a team",
    "setUpTeam": "Set up team",
    "submission": "Submission",
    "deadline": "{{round}} deadline",
    "notSet": "Not set",
    "advanced": {
      "badge": "Advanced to the final round",
      "description": "Congratulations, your team advanced. Complete your final-round submission before its deadline."
    },
    "header": {
      "individualEntry": "Individual entry",
      "teamEntry": "Team entry",
      "noTeamYet": "No team yet",
      "stageLine": "{{type}} · Current stage: {{stage}}",
      "stage": {
        "registration": "Registration",
        "team": "Team setup",
        "submission": "Submission",
        "waiting": "Awaiting results",
        "done": "Competition ended",
        "results": "Results published"
      }
    },
    "team": {
      "notCreated": "No team created or joined yet",
      "pending": "Pending",
      "notStarted": "Not started"
    },
    "submissionState": {
      "notStarted": "Not started",
      "viewSubmission": "View submission",
      "blockedByTeam": "Opens after team setup",
      "blockedByRegistration": "Opens after registration",
      "notOpenYet": "Submission isn't open yet",
      "missed": "Submission has closed"
    },
    "progress": {
      "title": "Your progress",
      "registration": "Registration",
      "registrationNeedsUpdate": "Registration (needs update)",
      "team": "Team",
      "submission": "Submission",
      "judging": "Judging",
      "results": "Results",
      "compact": "Step {{current}} of {{total}}"
    },
    "details": {
      "title": "Participation details"
    },
    "nextStep": {
      "title": "Next step",
      "registrationTask": "Complete your registration details",
      "registrationCta": "Go to registration details",
      "teamTask": "Create or join a team",
      "submissionTask": "Complete your {{round}} submission",
      "currentSubmission": "Current entry: {{state}}",
      "deadlineDetail": "Deadline: {{date}}",
      "continueSubmission": "Continue your submission",
      "startSubmission": "Start your submission",
      "checkAndSubmit": "Check and submit",
      "finalStartSubmission": "Start preparing your final entry",
      "finalContinueSubmission": "Continue your final entry",
      "finalCheckAndSubmit": "Check and submit your final entry",
      "notOpenYetTask": "Submission isn't open yet",
      "missedTask": "Submission has closed",
      "expiredTask": "Submission has closed. You can no longer formally submit.",
      "finalExpiredTask": "Final-round submission has closed. You can no longer formally submit.",
      "waitingTask": "Awaiting judging results",
      "finalWaitingTask": "Awaiting final-round judging results",
      "correctionTask": "Your entry needs a correction",
      "correctionCta": "View and fix your entry",
      "resultPublishedTask": "Results are published",
      "viewResults": "View results",
      "done": "This competition has concluded. Thank you for participating."
    },
    "nextMilestone": {
      "title": "Next Milestone",
      "withRound": "{{round}} - {{milestone}}",
      "daysRemaining": "{{count}} days left"
    },
    "actionPriority": {
      "p0": "Urgent",
      "p1": "Due soon",
      "p2": "Normal"
    }
  },
  "team": {
    "title": "Team",
    "loading": "Loading team…",
    "subtitle": "Manage your team members, invitations and participation details.",
    "choice": {
      "title": "Set up your team",
      "description": "You need to join a team that follows the competition's rules before you can continue.",
      "createTitle": "Create a new team",
      "createDescription": "Create your own team and invite other members.",
      "createCta": "Create team",
      "joinTitle": "Join an existing team",
      "joinDescription": "Use the secure invitation link a teammate sent you to join their team.",
      "joinCta": "How to join",
      "joinInfoTitle": "How do I join a team?",
      "joinInfo": "Open the invitation link your teammate sent you.",
      "joinInfoDetail": "You'll see the team's details first, then confirm to join."
    },
    "notices": {
      "created": "Team created. Next: invite your teammates.",
      "saved": "Team saved successfully.",
      "inviteRevoked": "Invite revoked.",
      "memberRemoved": "Member removed.",
      "leadershipTransferred": "Leadership transferred.",
      "formSaved": "Team form saved successfully.",
      "linkCopied": "Invitation link copied",
      "copyFailed": "Could not automatically copy the invitation link. Please copy it manually."
    },
    "errors": {
      "create": "Unable to create team.",
      "save": "Unable to save team.",
      "load": "Unable to load team.",
      "createInvite": "Unable to create invite.",
      "resendInvite": "Unable to resend invite.",
      "revokeInvite": "Unable to revoke invite.",
      "removeMember": "Unable to remove member.",
      "transferLeadership": "Unable to transfer leadership.",
      "saveForm": "Unable to save team form."
    },
    "create": {
      "title": "Create a team",
      "creating": "Creating team…",
      "submit": "Create team"
    },
    "fields": {
      "name": "Team name",
      "category": "Category",
      "selectPlaceholder": "Select…"
    },
    "details": {
      "title": "Team details",
      "saving": "Saving team…",
      "save": "Save team",
      "leaderOnly": "Only the team leader can edit team details."
    },
    "members": {
      "title": "Members",
      "memberCount": "{{count}} members",
      "removing": "Removing…",
      "remove": "Remove",
      "removeConfirmTitle": "Remove this member?",
      "removeScope": "Takes effect immediately.",
      "removeImpact": "Once removed, {{name}} will no longer be a member of this team. Rejoining requires accepting a new, valid invitation.",
      "removeSuccess": "Removed {{name}}.",
      "inviteTeammates": "Invite teammates to join this team.",
      "transferLeadership": "Transfer leadership",
      "selectMember": "Select a student member…",
      "reason": "Reason",
      "transferring": "Transferring…",
      "transferSubmit": "Transfer leadership",
      "transferConfirmTitle": "Transfer leadership to {{name}}?",
      "transferScope": "Takes effect immediately.",
      "transferImpact": "{{name}} will become the team leader, and you will become a regular member.",
      "transferSuccess": "Leadership transferred to {{name}}."
    },
    "results": {
      "title": "My Results",
      "loading": "Loading results…",
      "private": "The organizer hasn't opened result viewing yet — contact them if you need to know your results.",
      "empty": "No published results yet.",
      "publishedAt": "Published: {{time}}",
      "finalScore": "Final score: {{score}}",
      "rank": "Rank: #{{rank}}",
      "awardTier": "Award: {{tier}}",
      "excluded": "Did not meet the scoring quorum for this round; not included in this result set.",
      "awardTiers": {
        "first": "First Place",
        "second": "Second Place",
        "third": "Third Place",
        "honorable": "Honorable Mention",
        "vacant": "Vacant"
      },
      "breakdown": "Judges' average {{judge}}, staff-entered adjustment {{manual}}",
      "manualItem": {
        "add": "{{name}}: +{{value}}",
        "deduct": "{{name}}: -{{value}}"
      }
    },
    "invitations": {
      "title": "Invite teammates",
      "description": "Invite a teammate by email, or share an invitation link.",
      "emailLabel": "Email",
      "emailPlaceholder": "teammate@example.com",
      "sendEmailInvite": "Send invite",
      "sendingEmailInvite": "Sending invite…",
      "emailSentHeading": "Invite sent to {{email}}",
      "resend": "Resend",
      "orDivider": "or",
      "shareLinkHeading": "Share invitation link",
      "creating": "Creating invitation link…",
      "createOneTime": "+ Create invitation link",
      "createAnother": "+ Create another link",
      "revoke": "Revoke invitation",
      "currentHeading": "Invitation link active",
      "linkLabel": "Invitation link",
      "noneYet": "No active invitation link right now.",
      "tokenUnavailable": "This invite's link can only be copied once, at the moment it's created. To share it again, revoke this one and create a new invitation link.",
      "historyToggle": "Invitation history {{count}}",
      "revokeConfirmTitle": "Revoke this invitation?",
      "revokeConfirmTarget": "The invitation link created on {{date}}",
      "revokeConfirmTargetWithUrl": "The invitation link created on {{date}} ({{url}})",
      "revokeScope": "Only this invitation link — no effect on the rest of the team or other invites.",
      "revokeImpact": "Once revoked, this link can no longer be used to join the team. Members who already joined are unaffected.",
      "revokeSuccess": "Invitation revoked.",
      "copy": "Copy invitation link",
      "copyAria": "Copy invitation link",
      "copied": "✓ Copied",
      "open": "Open link",
      "openAria": "Open invitation link in a new tab",
      "createdAt": "Created {{date}}",
      "status": {
        "active": "Active",
        "revoked": "Revoked",
        "expired": "Expired",
        "exhausted": "Fully used"
      },
      "usage": {
        "unused": "Not used yet",
        "used": "Used {{count}}/{{max}} times",
        "unlimited": "Used {{count}} times"
      },
      "expiresIn": {
        "days": "Expires in {{count}} days",
        "today": "Expires today",
        "expired": "Expired"
      }
    },
    "form": {
      "title": "Team participation details",
      "loading": "Loading team form…",
      "none": "This competition doesn't need any additional team details.",
      "formLabel": "Form",
      "loadingFields": "Loading form fields…",
      "save": "Save team form"
    },
    "currentMemberRole": "Your team role: {{role}}"
  },
  "submission": {
    "title": "Submission",
    "loading": "Loading submission…",
    "teamRequired": "You need a team before you can start a submission.",
    "createTeam": "Create or join a team",
    "noForm": "This competition has no submission form configured yet.",
    "rounds": {
      "tablist": "Rounds"
    },
    "start": {
      "title": "Start your submission",
      "description": "You haven't started a submission for this competition yet.",
      "button": "Start submission",
      "starting": "Starting…"
    },
    "status": {
      "label": "Status",
      "submittedAt": "Submitted at",
      "notSubmitted": "Not submitted yet",
      "readyNotSubmitted": "Ready, not yet formally submitted"
    },
    "waiting": {
      "nothingToDo": "There is nothing you need to do right now."
    },
    "locked": {
      "submitted": "This submission has been submitted and can no longer be edited.",
      "locked": "This submission is locked and can no longer be edited.",
      "withdrawn": "This submission has been withdrawn.",
      "disqualified": "This submission has been disqualified."
    },
    "success": {
      "title": "Submission sent",
      "description": "Your submission has been saved for this formal submit.",
      "submittedAtLabel": "Submitted at",
      "receiptLabel": "Submission receipt",
      "viewReceipt": "View submission receipt",
      "nextStepLabel": "Next step",
      "nextStepTask": "Waiting for judging results"
    },
    "draftCreated": {
      "title": "Draft created",
      "description": "You can come back to it any time — it stays editable until you formally submit.",
      "cta": "Keep working on it"
    },
    "correctionRequired": {
      "title": "Your entry needs a correction",
      "description": "The organizer has asked you to fix or add something. Please review and resubmit.",
      "reasonLabel": "Reason:"
    },
    "resultPublished": {
      "title": "Results are published",
      "cta": "View results"
    },
    "receipt": {
      "title": "Submission receipt",
      "loading": "Loading receipt…",
      "empty": "No receipt yet.",
      "id": "Receipt ID",
      "version": "Version",
      "submittedAt": "Submitted at",
      "timezone": "Timezone",
      "files": "File manifest",
      "noFiles": "No files were attached to this version.",
      "status": {
        "active": "Active",
        "withdrawn": "Withdrawn",
        "disqualified": "Disqualified"
      }
    },
    "form": {
      "title": "Answers",
      "loadingFields": "Loading form fields…",
      "save": "Save draft"
    },
    "actions": {
      "title": "Submit your entry",
      "markReadyDescription": "Mark your draft ready, then submit it as your final entry. You can still edit while it's a draft or ready.",
      "markReady": "Mark ready to submit",
      "backToDraft": "Return to draft",
      "submit": "Submit final entry",
      "submitting": "Submitting…",
      "submitDialogTitle": "Submit final entry",
      "submitDialogScope": "Only this submission (Round {{round}}, due {{deadline}}) is affected.",
      "submitDialogImpact": "Formally submitting creates an immutable final version and locks the submission status to submitted; you cannot edit it yourself afterward. If the organizer decides a correction is needed, they can return it via the existing correction flow, and you will see that explanation on this page.",
      "submitDialogSuccess": "Entry submitted.",
      "checkAndSubmit": "Check and submit",
      "confirmSubmit": "Confirm formal submit",
      "returnToReview": "Back to review",
      "uploadPending": "A file is still being processed. Please try again shortly."
    },
    "notices": {
      "saved": "Draft saved."
    },
    "errors": {
      "load": "Unable to load submission.",
      "create": "Unable to start submission.",
      "save": "Unable to save draft.",
      "status": "Unable to update status.",
      "submit": "Unable to submit."
    },
    "workspace": {
      "completion": "Completion: {{complete}} / {{total}} required items",
      "required": "Required",
      "optional": "Optional",
      "edit": "Edit",
      "collapse": "Collapse",
      "collapseField": "Collapse {{label}}",
      "empty": "(not filled in)",
      "status": {
        "complete": "Complete",
        "incomplete": "Incomplete",
        "uploading": "Uploading",
        "optional_empty": "Optional, not filled in",
        "failed": "Upload failed, please retry"
      }
    },
    "preflight": {
      "title": "Preflight review",
      "requiredHeading": "Required items",
      "optionalHeading": "Optional items",
      "itemLine": "{{label}}: {{state}}",
      "notComplete": "Not complete",
      "optionalNotProvided": "{{label}} not provided",
      "goToField": "Go to field",
      "competitionLabel": "Competition",
      "teamLabel": "Team",
      "roundLabel": "Round",
      "deadlineLabel": "Deadline",
      "notSet": "Not set",
      "backToEdit": "Back to edit",
      "confirm": "Formally submit"
    },
    "teamDisqualified": "Your team has been disqualified and can no longer start or edit a submission in any round. Please contact the organizer."
  },
  "eligibility": {
    "title": "Student Eligibility Verification",
    "subtitle": "Upload your enrollment proof and check your verification status.",
    "loading": "Loading eligibility data…",
    "unavailable": "Eligibility data is unavailable right now.",
    "notRequired": "This competition does not require student eligibility verification.",
    "backToWorkspace": "Back to competition workspace",
    "status": {
      "missing": "Not uploaded",
      "pending": "Under review",
      "verified": "Verified",
      "rejected": "Rejected",
      "expired": "Expired"
    },
    "ownStatus": {
      "title": "My eligibility status",
      "pendingHint": "We've received your enrollment proof and it's under review. This does not affect your completed registration.",
      "rejectionReasonTitle": "Rejection reason",
      "expiredHint": "Your previous enrollment proof has expired. Please upload a new one.",
      "uploadedAt": "Uploaded: {{date}}",
      "download": "Download to view",
      "downloadError": "Could not create a download link. Please try again later."
    },
    "upload": {
      "title": "Upload enrollment proof",
      "replaceTitle": "Replace enrollment proof",
      "proofType": "Proof type",
      "proofTypeOptions": {
        "enrollment_certificate": "Enrollment certificate",
        "student_id": "Student ID card",
        "other_approved": "Other approved document"
      },
      "schoolName": "School name",
      "departmentName": "Department (optional)",
      "studentNumber": "Student number (optional)",
      "selectFile": "Choose file",
      "replace": "Replace file",
      "replaceWarning": "Your current enrollment proof is under review. Uploading a new file will restart the review process with the new file.",
      "cancelReplace": "Cancel",
      "prefillNotice": "Your current eligibility details are prefilled below.",
      "uploading": "Uploading…",
      "finalizing": "Processing…",
      "cancel": "Cancel upload",
      "allowedTypes": "Supported formats: {{extensions}}. Maximum file size: 10 MB.",
      "errors": {
        "fileTypeNotAllowed": "This file type is not allowed.",
        "tooLarge": "The file must not exceed 10 MB.",
        "empty": "The file is empty. Please choose a valid document.",
        "extensionNotAllowed": "Only these file types are allowed: {{extensions}}",
        "generic": "Upload failed. Please try again.",
        "genericWithDetail": "Upload failed: {{detail}}"
      }
    },
    "teamSummary": {
      "title": "Team eligibility completion",
      "submittedProgress": "Submitted proof {{submitted}} / {{required}}",
      "approvedProgress": "Approved {{approved}} / {{required}}",
      "allApproved": "✓ Every required member has completed eligibility verification.",
      "you": "you",
      "leader": "leader",
      "notRequired": "Not required"
    }
  },
  "result": {
    "title": "View results",
    "loading": "Loading…",
    "teamRequired": "You need to set up a team before you can view results.",
    "notAdvanced": "Your team did not advance to the next round. Thank you for participating.",
    "errors": {
      "load": "Unable to load this data. Please try again later."
    },
    "cta": {
      "prepareFinal": "Start preparing your final entry",
      "backToWorkspace": "Back to the competition overview"
    },
    "disqualified": {
      "title": "Your team has been disqualified",
      "body": "Your team can no longer submit or edit work, and its scores are not counted in the results. Please contact the organizer if you have questions."
    }
  },
  "workspace": {
    "nav": "Participant task navigation",
    "openNav": "Open participant task menu",
    "overview": "Overview",
    "groups": {
      "tasks": "Participant Tasks",
      "results": "Results",
      "information": "Information"
    },
    "items": {
      "eligibility": "Student Eligibility",
      "team": "My Team",
      "submission": "My Submission",
      "result": "My Result",
      "information": "Competition Information"
    },
    "identity": {
      "individual": "Participant",
      "withTeam": "Participant · {{team}} · {{role}}"
    },
    "accessDenied": {
      "title": "You can no longer open this competition's Participant workspace",
      "description": "You currently cannot enter this competition with this role.",
      "back": "Back to My Competitions"
    }
  },
  "information": {
    "title": "Competition Information",
    "loading": "Loading competition information…",
    "unavailable": "Unable to load competition information right now.",
    "schedule": {
      "title": "Schedule and Milestones",
      "empty": "No schedule has been announced yet."
    },
    "announcements": {
      "title": "Announcements"
    },
    "rules": {
      "title": "Rules and Resources"
    }
  }
}
